import * as z from 'zod';
import { companyBackgroundFileTypeDef, corporateFinancialsFileTypeDef, personalFinancialsFileTypeDef, wipFileTypeDef, bankInformationFileTypeDef, indemnityFileTypeDef, dateTimeSchema, suretyAccountAttachmentFileTypeSchema, IndemnityAttachmentTypeLabelMap, bankInformationAttachmentTypeLabelMap, wipAttachmentTypeLabelMap, personalFinancialsAttachmentTypeLabelMap, corporateFinancialsAttachmentTypeLabelMap, companyBackgroundAttachmentTypeLabelMap } from '../../enums.mjs';
import '../../types/actor/actor.mjs';
import '../../types/address/address.mjs';
import '../../types/credit-report/company-credit-report.mjs';
import '../../types/credit-report/individual-credit-report.mjs';
import '../../types/currency/currency.mjs';
import '../../types/entityRelation/entity-relation.mjs';
import '../../types/financial-formula/adjustable-value.mjs';
import '../../types/financial-formula/adjustable-values-update.mjs';
import '../../types/financial-formula/adjustment.mjs';
import '../../types/financial-formula/formula.mjs';
import '@inrev/core';
import '../../types/is-encrypted.mjs';
import '../../types/naics-codes/naics-codes.mjs';
import { decisioningFactorGroupId, userId, decisioningFactorId, suretyAccountId, proConId, proConRelationshipId, dataNeededId, attachmentId, decisionId, rateSchemaV2, suretyAccountUnderwritingId } from '../../types/opaque.mjs';
import '../../types/surety/account/requested-limits.mjs';
import '../../types/surety/account/surety-account.types.mjs';
import '../../types/surety/application-extraction/application-extraction-output.mjs';
import '../../types/surety/application-extraction/application-extraction-webhook.mjs';
import '../../types/surety/application-extraction/application-review.mjs';
import '../../types/surety/bond/surety-bond.types.mjs';
import '../../types/surety/bond-form/issued/issued-surety-bond-form.types.mjs';
import '../../types/surety/bond-form/surety-bond-form.data.mjs';
import '../../types/surety/bond-request/bond-request-extraction-output.mjs';
import '../../types/surety/bond-request/bond-request-extraction-webhook.mjs';
import '../../types/surety/contract/contract-extraction-output.mjs';
import '../../types/surety/contract/contract-extraction-webhook.mjs';
import '../../types/surety/financial-statement/financial-period/balance-sheet-data.mjs';
import '../../types/surety/financial-statement/financial-period/financial-period-extraction-webhook.mjs';
import '../../types/surety/financial-statement/financial-period/income-statement-data.mjs';
import '../../types/surety/financial-statement/financial-period/income-statement-fields.mjs';
import '../../types/surety/financial-statement/financial-period/income-statement-with-adjustments.mjs';
import '../../types/surety/financial-statement/financial-period/line-item-group.mjs';
import '../../types/surety/financial-statement/financial-period/section-keys.mjs';
import '../../types/surety/financial-statement/financial-period/statement-metadata.mjs';
import '../../types/surety/financial-statement/financial-period/view-shapes.mjs';
import '../../types/surety/financial-statement/financial-statement-schemas.mjs';
import '../../types/surety/indemnity/indemnity-agreement.mjs';
import '../../types/surety/quote/surety-quote.types.mjs';
import '../../types/surety/wip-schedule/wip-schedule-data.mjs';
import '../../types/surety/wip-schedule/wip-schedule-extraction-webhook.mjs';
import '../../types/surety/work-in-progress-statement/surety-work-in-progress-statement.mjs';
import '../../types/user/user.types.mjs';
import { positiveCurrencySchema } from '../../types/utils.mjs';

const proConKindSchema = z.enum(["pro", "con"]);
const proConImportanceSchema = z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
]);
const OVERRIDE_IMPORTANCE = 4;
const lensSchema = z.enum(["Character", "Capacity", "Capital"]);
const proConRelationshipTypeSchema = z.enum(["mitigates", "compounds", "explains"]);
const canRelate = (type, source, target) => {
    switch (type) {
        case "mitigates":
            return source.kind !== target.kind;
        case "compounds":
            return source.kind === target.kind;
        case "explains":
            return true;
    }
};
const dataNeededAssigneeSchema = z.enum(["Agent", "Underwriter", "Operations"]);
const dataNeededTypeSchema = z.enum(["Document", "Question", "Task"]);
const dataNeededFileTypeSections = [
    { name: "Company", fileTypes: companyBackgroundFileTypeDef },
    { name: "Company Financials", fileTypes: corporateFinancialsFileTypeDef },
    { name: "Personal Financials", fileTypes: personalFinancialsFileTypeDef },
    { name: "Work in Progress", fileTypes: wipFileTypeDef },
    { name: "Banking", fileTypes: bankInformationFileTypeDef },
    { name: "Indemnity", fileTypes: indemnityFileTypeDef },
];
const dataNeededFileTypes = dataNeededFileTypeSections.flatMap((section) => section.fileTypes);
const inFileTypePickerOrder = (fileTypes) => {
    const picked = new Set(fileTypes);
    return dataNeededFileTypes.filter((fileType) => picked.has(fileType));
};
const fileTypeLabels = {
    ...companyBackgroundAttachmentTypeLabelMap,
    ...corporateFinancialsAttachmentTypeLabelMap,
    ...personalFinancialsAttachmentTypeLabelMap,
    ...wipAttachmentTypeLabelMap,
    ...bankInformationAttachmentTypeLabelMap,
    ...IndemnityAttachmentTypeLabelMap,
};
const fileTypeLabel = (fileType) => fileTypeLabels[fileType];
const dataNeededImportanceSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);
const dataNeededStatusSchema = z.enum(["open", "received", "closed"]);
const dataNeededClosedReasonSchema = z.enum(["Not Needed", "Waived", "Error"]);
const factorValueTypeSchema = z.enum([
    "number",
    "currency",
    "percent",
    "ratio",
    "text",
    "boolean",
    "date",
]);
const factorValueSchema = z.union([z.number(), z.string(), z.boolean()]);
const elementStatusSchema = z.enum([
    "present",
    "missing_source",
    "not_applicable",
    "ambiguous",
    "unmapped",
]);
const elementEntrySchema = z.discriminatedUnion("status", [
    z.object({ status: z.literal("present"), value: factorValueSchema }),
    z.object({ status: elementStatusSchema.exclude(["present"]) }),
]);
const observedValueSourceSchema = z.enum(["snapshot", "underwriter"]);
const isValueForType = (valueType, value) => {
    switch (valueType) {
        case "number":
        case "currency":
        case "percent":
        case "ratio":
            return typeof value === "number" && Number.isFinite(value);
        case "text":
            return typeof value === "string" && value.trim().length > 0;
        case "boolean":
            return typeof value === "boolean";
        case "date":
            return typeof value === "string" && z.iso.date().safeParse(value).success;
    }
};
const requiresRationale = (importance) => importance === OVERRIDE_IMPORTANCE;
var FactorGroup;
(function (FactorGroup) {
    (function (Get) {
        (function (Response) {
            Response.schema = z.object({
                id: decisioningFactorGroupId,
                lens: lensSchema,
                name: z.string(),
                sortOrder: z.number().int(),
                isSeed: z.boolean(),
                createdByUserId: userId.nullable(),
                createdAt: dateTimeSchema,
            });
        })(Get.Response || (Get.Response = {}));
    })(FactorGroup.Get || (FactorGroup.Get = {}));
    (function (Create) {
        (function (Request) {
            Request.schema = z.object({
                lens: lensSchema,
                name: z.string().trim().min(1),
            });
        })(Create.Request || (Create.Request = {}));
    })(FactorGroup.Create || (FactorGroup.Create = {}));
})(FactorGroup || (FactorGroup = {}));
var Factor;
(function (Factor) {
    (function (Get) {
        (function (Response) {
            Response.schema = z.object({
                id: decisioningFactorId,
                key: z.string().nullable(),
                name: z.string(),
                group: FactorGroup.Get.Response.schema,
                valueType: factorValueTypeSchema,
                sortOrder: z.number().int(),
                isSeed: z.boolean(),
                createdByUserId: userId.nullable(),
                createdAt: dateTimeSchema,
            });
        })(Get.Response || (Get.Response = {}));
    })(Factor.Get || (Factor.Get = {}));
    (function (Create) {
        (function (Request) {
            Request.schema = z.object({
                name: z.string().trim().min(1),
                groupId: decisioningFactorGroupId,
                valueType: factorValueTypeSchema,
            });
        })(Create.Request || (Create.Request = {}));
    })(Factor.Create || (Factor.Create = {}));
})(Factor || (Factor = {}));
var ProCon;
(function (ProCon) {
    (function (Get) {
        (function (Response) {
            Response.schema = z.object({
                id: proConId,
                accountId: suretyAccountId,
                kind: proConKindSchema,
                factor: Factor.Get.Response.schema,
                detail: z.string().nullable(),
                importance: proConImportanceSchema,
                observedValue: factorValueSchema.nullable(),
                observedValueSource: observedValueSourceSchema.nullable(),
                rank: z.number().int().nonnegative(),
                context: z.string(),
                retiredAt: dateTimeSchema.nullable(),
                retiredByUserId: userId.nullable(),
                createdAt: dateTimeSchema,
                updatedAt: dateTimeSchema,
            });
        })(Get.Response || (Get.Response = {}));
    })(ProCon.Get || (ProCon.Get = {}));
    (function (Create) {
        (function (Request) {
            Request.schema = z.object({
                kind: proConKindSchema,
                factorId: decisioningFactorId,
                detail: z.string().trim().nullable(),
                importance: proConImportanceSchema,
                observedValue: factorValueSchema.nullable().optional(),
            });
        })(Create.Request || (Create.Request = {}));
    })(ProCon.Create || (ProCon.Create = {}));
    (function (Update) {
        (function (Request) {
            Request.schema = z.object({
                factorId: decisioningFactorId.optional(),
                detail: z.string().trim().nullable().optional(),
                importance: proConImportanceSchema.optional(),
                observedValue: factorValueSchema.nullable().optional(),
                retired: z.literal(true).optional(),
            });
        })(Update.Request || (Update.Request = {}));
    })(ProCon.Update || (ProCon.Update = {}));
    (function (ObservedValues) {
        (function (Response) {
            Response.schema = z.array(z.object({ factorId: decisioningFactorId, value: factorValueSchema }));
        })(ObservedValues.Response || (ObservedValues.Response = {}));
    })(ProCon.ObservedValues || (ProCon.ObservedValues = {}));
    (function (Reorder) {
        (function (Request) {
            Request.schema = z.object({
                kind: proConKindSchema,
                importance: proConImportanceSchema,
                ids: z.array(proConId).min(1),
            });
        })(Reorder.Request || (Reorder.Request = {}));
    })(ProCon.Reorder || (ProCon.Reorder = {}));
})(ProCon || (ProCon = {}));
var ProConRelationship;
(function (ProConRelationship) {
    (function (Get) {
        (function (Response) {
            Response.schema = z.object({
                id: proConRelationshipId,
                accountId: suretyAccountId,
                sourceProConId: proConId,
                targetProConId: proConId,
                type: proConRelationshipTypeSchema,
                rationale: z.string(),
                createdAt: dateTimeSchema,
                updatedAt: dateTimeSchema,
            });
        })(Get.Response || (Get.Response = {}));
    })(ProConRelationship.Get || (ProConRelationship.Get = {}));
    (function (Create) {
        (function (Request) {
            Request.schema = z
                .object({
                sourceProConId: proConId,
                targetProConId: proConId,
                type: proConRelationshipTypeSchema,
                rationale: z.string().trim().min(1),
            })
                .refine(({ sourceProConId, targetProConId }) => sourceProConId !== targetProConId, {
                message: "A factor cannot relate to itself",
                path: ["targetProConId"],
            });
        })(Create.Request || (Create.Request = {}));
    })(ProConRelationship.Create || (ProConRelationship.Create = {}));
})(ProConRelationship || (ProConRelationship = {}));
var DataNeeded;
(function (DataNeeded) {
    (function (Get) {
        (function (Response) {
            Response.schema = z.object({
                id: dataNeededId,
                accountId: suretyAccountId,
                assignee: dataNeededAssigneeSchema,
                type: dataNeededTypeSchema,
                fileTypes: z.array(suretyAccountAttachmentFileTypeSchema),
                title: z.string(),
                rationale: z.string(),
                description: z.string().nullable(),
                importance: dataNeededImportanceSchema,
                proConId: proConId.nullable(),
                context: z.string(),
                status: dataNeededStatusSchema,
                closedReason: dataNeededClosedReasonSchema.nullable(),
                resolvedAt: dateTimeSchema.nullable(),
                resolvedByUserId: userId.nullable(),
                fulfillmentAttachmentIds: z.array(attachmentId),
                fulfillmentAnswer: z.string().nullable(),
                createdAt: dateTimeSchema,
                updatedAt: dateTimeSchema,
            });
        })(Get.Response || (Get.Response = {}));
    })(DataNeeded.Get || (DataNeeded.Get = {}));
    (function (Create) {
        (function (Request) {
            Request.schema = z.object({
                assignee: dataNeededAssigneeSchema,
                type: dataNeededTypeSchema,
                fileTypes: z.array(suretyAccountAttachmentFileTypeSchema),
                title: z.string().trim().min(1),
                rationale: z.string().trim().min(1),
                description: z.string().trim().nullable(),
                importance: dataNeededImportanceSchema,
                proConId: proConId.nullable(),
            });
        })(Create.Request || (Create.Request = {}));
    })(DataNeeded.Create || (DataNeeded.Create = {}));
    (function (Update) {
        (function (Request) {
            Request.schema = z.object({
                assignee: dataNeededAssigneeSchema.optional(),
                type: dataNeededTypeSchema.optional(),
                fileTypes: z.array(suretyAccountAttachmentFileTypeSchema).optional(),
                title: z.string().trim().min(1).optional(),
                rationale: z.string().trim().min(1).optional(),
                description: z.string().trim().nullable().optional(),
                importance: dataNeededImportanceSchema.optional(),
                proConId: z.null().optional(),
            });
        })(Update.Request || (Update.Request = {}));
    })(DataNeeded.Update || (DataNeeded.Update = {}));
    (function (Resolve) {
        (function (Request) {
            Request.schema = z.discriminatedUnion("status", [
                z.object({
                    status: z.literal("received"),
                    attachmentIds: z.array(attachmentId).default([]),
                    answer: z.string().trim().optional(),
                }),
                z.object({ status: z.literal("closed"), closedReason: dataNeededClosedReasonSchema }),
            ]);
        })(Resolve.Request || (Resolve.Request = {}));
    })(DataNeeded.Resolve || (DataNeeded.Resolve = {}));
})(DataNeeded || (DataNeeded = {}));
const decisionStageSchema = z.enum([
    "initial_submission_follow_up",
    "post_submission_follow_up",
    "approve",
    "decline",
    "close",
]);
const decisionFollowUpStages = [
    "initial_submission_follow_up",
    "post_submission_follow_up",
];
const isFollowUpStage = (stage) => decisionFollowUpStages.some((followUpStage) => followUpStage === stage);
const decisionBlockedReason = (stage, board) => {
    if (stage === "decline" && !board.proCons.some((proCon) => proCon.kind === "con")) {
        return "Add the cons that drove this decline first";
    }
    if (isFollowUpStage(stage) && !board.dataNeeded.some((item) => item.status === "open")) {
        return "Add the data needed for this follow-up first";
    }
    return null;
};
var Decision;
(function (Decision) {
    let SupportingData;
    (function (SupportingData) {
        SupportingData.SCHEMA_VERSION = 2;
        SupportingData.schema = z.object({
            schemaVersion: z.literal(SupportingData.SCHEMA_VERSION),
            proCons: z.array(ProCon.Get.Response.schema),
            relationships: z.array(ProConRelationship.Get.Response.schema),
            dataNeeded: z.array(DataNeeded.Get.Response.schema),
        });
        let V2;
        (function (V2) {
            const factorGroup = z.object({
                id: decisioningFactorGroupId,
                lens: z.enum(["Character", "Capacity", "Capital"]),
                name: z.string(),
                sortOrder: z.number(),
                isSeed: z.boolean(),
                createdByUserId: userId.nullable(),
                createdAt: dateTimeSchema,
            });
            const factor = z.object({
                id: decisioningFactorId,
                key: z.string().nullable(),
                name: z.string(),
                group: factorGroup,
                valueType: z.enum(["number", "currency", "percent", "ratio", "text", "boolean", "date"]),
                sortOrder: z.number(),
                isSeed: z.boolean(),
                createdByUserId: userId.nullable(),
                createdAt: dateTimeSchema,
            });
            const proCon = z.object({
                id: proConId,
                accountId: suretyAccountId,
                kind: z.enum(["pro", "con"]),
                factor,
                detail: z.string().nullable(),
                importance: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
                observedValue: z.union([z.number(), z.string(), z.boolean()]).nullable(),
                observedValueSource: z.enum(["snapshot", "underwriter"]).nullable(),
                rank: z.number(),
                context: z.string(),
                retiredAt: dateTimeSchema.nullable(),
                retiredByUserId: userId.nullable(),
                createdAt: dateTimeSchema,
                updatedAt: dateTimeSchema,
            });
            const relationship = z.object({
                id: proConRelationshipId,
                accountId: suretyAccountId,
                sourceProConId: proConId,
                targetProConId: proConId,
                type: z.enum(["mitigates", "compounds", "explains"]),
                rationale: z.string(),
                createdAt: dateTimeSchema,
                updatedAt: dateTimeSchema,
            });
            const isFileType = (value) => suretyAccountAttachmentFileTypeSchema.safeParse(value).success;
            const dataNeeded = z.object({
                id: dataNeededId,
                accountId: suretyAccountId,
                assignee: z.enum(["Agent", "Underwriter", "Operations"]),
                type: z.enum(["Document", "Question", "Task"]),
                fileTypes: z.array(z.string()).transform((values) => values.filter(isFileType)),
                title: z.string(),
                rationale: z.string(),
                description: z.string().nullable(),
                importance: z.union([z.literal(1), z.literal(2), z.literal(3)]),
                proConId: proConId.nullable(),
                context: z.string(),
                status: z.enum(["open", "received", "closed"]),
                closedReason: z.enum(["Not Needed", "Waived", "Error"]).nullable(),
                resolvedAt: dateTimeSchema.nullable(),
                resolvedByUserId: userId.nullable(),
                fulfillmentAttachmentIds: z.array(attachmentId),
                fulfillmentAnswer: z.string().nullable(),
                createdAt: dateTimeSchema,
                updatedAt: dateTimeSchema,
            });
            V2.schema = z.object({
                schemaVersion: z.literal(2),
                proCons: z.array(proCon),
                relationships: z.array(relationship),
                dataNeeded: z.array(dataNeeded),
            });
        })(V2 = SupportingData.V2 || (SupportingData.V2 = {}));
        (function (Stored) {
            Stored.schema = z.discriminatedUnion("schemaVersion", [V2.schema]);
        })(SupportingData.Stored || (SupportingData.Stored = {}));
    })(SupportingData = Decision.SupportingData || (Decision.SupportingData = {}));
    let DataNeededCurrent;
    (function (DataNeededCurrent) {
        DataNeededCurrent.schema = DataNeeded.Get.Response.schema.pick({
            id: true,
            status: true,
            closedReason: true,
            resolvedAt: true,
            fulfillmentAttachmentIds: true,
            fulfillmentAnswer: true,
        });
    })(DataNeededCurrent = Decision.DataNeededCurrent || (Decision.DataNeededCurrent = {}));
    (function (Get) {
        (function (Response) {
            Response.schema = z.object({
                id: decisionId,
                accountId: suretyAccountId,
                underwritingId: suretyAccountUnderwritingId.nullable(),
                underwriterUserId: userId,
                stage: decisionStageSchema,
                rationale: z.string(),
                rate: rateSchemaV2.nullable(),
                singleLimit: positiveCurrencySchema.nullable(),
                aggregateLimit: positiveCurrencySchema.nullable(),
                supersedesDecisionId: decisionId.nullable(),
                supportingData: SupportingData.Stored.schema,
                dataNeededCurrent: z.array(DataNeededCurrent.schema),
                createdAt: dateTimeSchema,
            });
        })(Get.Response || (Get.Response = {}));
    })(Decision.Get || (Decision.Get = {}));
    (function (Create) {
        (function (Request) {
            const base = {
                rationale: z.string().trim().min(1),
                supersedesDecisionId: decisionId.nullable(),
            };
            const limits = z.object({
                singleLimit: positiveCurrencySchema,
                aggregateLimit: positiveCurrencySchema,
            });
            Request.schema = z.discriminatedUnion("stage", [
                z.object({ ...base, stage: z.literal("initial_submission_follow_up") }),
                z.object({ ...base, stage: z.literal("post_submission_follow_up") }),
                z.object({ ...base, stage: z.literal("decline") }),
                z.object({ ...base, stage: z.literal("close") }),
                z
                    .object({
                    ...base,
                    ...limits.shape,
                    stage: z.literal("approve"),
                    rate: rateSchemaV2,
                })
                    .refine(({ singleLimit, aggregateLimit }) => aggregateLimit >= singleLimit, {
                    message: "Aggregate limit must be at least the single limit",
                    path: ["aggregateLimit"],
                    when: ({ value }) => limits.safeParse(value).success,
                }),
            ]);
        })(Create.Request || (Create.Request = {}));
    })(Decision.Create || (Decision.Create = {}));
})(Decision || (Decision = {}));
var Access;
(function (Access) {
    (function (Get) {
        (function (Response) {
            Response.schema = z.object({ enabled: z.boolean() });
        })(Get.Response || (Get.Response = {}));
    })(Access.Get || (Access.Get = {}));
})(Access || (Access = {}));

export { Access, DataNeeded, Decision, Factor, FactorGroup, OVERRIDE_IMPORTANCE, ProCon, ProConRelationship, canRelate, dataNeededAssigneeSchema, dataNeededClosedReasonSchema, dataNeededFileTypeSections, dataNeededImportanceSchema, dataNeededStatusSchema, dataNeededTypeSchema, decisionBlockedReason, decisionFollowUpStages, decisionStageSchema, elementEntrySchema, elementStatusSchema, factorValueSchema, factorValueTypeSchema, fileTypeLabel, inFileTypePickerOrder, isFollowUpStage, isValueForType, lensSchema, observedValueSourceSchema, proConImportanceSchema, proConKindSchema, proConRelationshipTypeSchema, requiresRationale };
